import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  SparklesIcon,
  BookOpenIcon,
  UserGroupIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useFriendsNotification } from '../contexts/FriendsNotificationContext';
import FeedbackButton from './FeedbackButton';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasUnreadRequests, unreadCount } = useFriendsNotification();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [activeTab, setActiveTab] = useState<string>('discover');

  useEffect(() => {
    const currentPath = location.pathname;
    
    if (currentPath === '/' || currentPath.startsWith('/recommendations')) {
      setActiveTab('discover');
    } else if (currentPath.startsWith('/search')) {
      setActiveTab('');
    } else if (currentPath.startsWith('/shelves') || currentPath.startsWith('/library') || currentPath.startsWith('/reviews')) {
      setActiveTab('library');
    } else if (currentPath.startsWith('/friends')) {
      setActiveTab('friends');
    } else if (currentPath.startsWith('/admin')) {
      setActiveTab('admin');
    } else if (currentPath.startsWith('/book/')) {
      setActiveTab('');
    } else if (currentPath.startsWith('/profile/')) {
      setActiveTab('');
    } else {
      setActiveTab('discover');
    }
  }, [location.pathname]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    if (user) {
      navigate(`/profile/${user.id}`);
    }
  };

  const handleLogout = () => {
    handleClose();
    logout();
    // Auth0 will redirect to home page after logout
  };

  const handleTabClick = (tab: string, route: string) => {
    setActiveTab(tab);
    // Reset tab state when clicking top-level navigation
    if (route === '/') {
      localStorage.setItem('discoverActiveTab', '0');
    } else if (route === '/library') {
      localStorage.setItem('libraryActiveTab', 'shelves');
    }
    navigate(route);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      background: '#fafafa',
    }}>
      {/* New Navigation Bar */}
      {user && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 30px',
          background: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          {/* Brand */}
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => handleTabClick('discover', '/')}
          >
            📚 BooksByHenry
          </Typography>
          
          {/* Navigation Tabs */}
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px 16px',
                borderRadius: '12px',
                color: activeTab === 'discover' ? 'white' : '#757575',
                background: activeTab === 'discover' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : 'transparent',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  background: activeTab === 'discover' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#f5f5f5',
                  color: activeTab === 'discover' ? 'white' : '#212121',
                }
              }}
              onClick={() => handleTabClick('discover', '/')}
            >
              <SparklesIcon style={{ width: 20, height: 20 }} />
              <span>Discover</span>
            </Box>
            
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px 16px',
                borderRadius: '12px',
                color: activeTab === 'library' ? 'white' : '#757575',
                background: activeTab === 'library' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : 'transparent',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  background: activeTab === 'library' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#f5f5f5',
                  color: activeTab === 'library' ? 'white' : '#212121',
                }
              }}
              onClick={() => handleTabClick('library', '/library')}
            >
              <BookOpenIcon style={{ width: 20, height: 20 }} />
              <span>Library</span>
            </Box>
            
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px 16px',
                borderRadius: '12px',
                color: activeTab === 'friends' ? 'white' : '#757575',
                background: activeTab === 'friends' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : 'transparent',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                fontWeight: 500,
                cursor: 'pointer',
                position: 'relative',
                '&:hover': {
                  background: activeTab === 'friends' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#f5f5f5',
                  color: activeTab === 'friends' ? 'white' : '#212121',
                }
              }}
              onClick={() => handleTabClick('friends', '/friends')}
            >
              <UserGroupIcon style={{ width: 20, height: 20 }} />
              <span>Friends</span>
              {hasUnreadRequests && (
                <Box
                  sx={{
                    backgroundColor: '#ff4444',
                    color: 'white',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    ml: 0.5,
                  }}
                >
                  {unreadCount}
                </Box>
              )}
            </Box>
            
            {user?.user_type === 'admin' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 16px',
                  borderRadius: '12px',
                  color: activeTab === 'admin' ? 'white' : '#757575',
                  background: activeTab === 'admin' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'transparent',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': {
                    background: activeTab === 'admin' 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#f5f5f5',
                    color: activeTab === 'admin' ? 'white' : '#212121',
                  }
                }}
                onClick={() => handleTabClick('admin', '/admin')}
              >
                <ShieldCheckIcon style={{ width: 20, height: 20 }} />
                <span>Admin</span>
              </Box>
            )}
          </Box>
          
          {/* Profile Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#757575' }}>
              Hi, {user.full_name || user.username}!
            </Typography>
            <Avatar
              src={user.profile_picture}
              sx={{
                width: 36,
                height: 36,
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
              onClick={handleMenu}
            >
              {!user.profile_picture && (user.full_name || user.username)?.charAt(0).toUpperCase()}
            </Avatar>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  mt: 1,
                }
              }}
            >
              <MenuItem disabled>
                <Typography variant="body2" sx={{ color: '#757575' }}>
                  @{user.username}
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleProfile}>My Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Box>
      )}
      
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      {/* Feedback button - always visible */}
      <FeedbackButton />
    </Box>
  );
};

export default Layout;